'use client'

import posthog from 'posthog-js'
import { PostHogProvider } from 'posthog-js/react'
import { useEffect } from 'react'
import { createClient } from '@/lib/supabase/client'

// O `!` em `process.env.NEXT_PUBLIC_POSTHOG_KEY!` era uma mentira pro
// TypeScript. Sem a variável, o init recebia `undefined` e o PostHog reclamava
// no console de toda página. E o barulho era o menor problema: a asserção
// escondia a pergunta que importa, que é se a analytics está ligada ou não
// neste ambiente.
//
// Agora é explícito. Sem chave, não inicializa e não identifica ninguém, e o
// aviso único abaixo diz que está DESLIGADA em vez de fingir que está ligada.
const POSTHOG_KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY

if (typeof window !== 'undefined') {
  if (POSTHOG_KEY) {
    posthog.init(POSTHOG_KEY, {
      api_host: 'https://us.i.posthog.com',
      capture_pageview: true,
      capture_pageleave: true,
      person_profiles: 'identified_only',
    })
  } else if (process.env.NODE_ENV !== 'production') {
    console.info('[analytics] NEXT_PUBLIC_POSTHOG_KEY ausente — PostHog desligado neste ambiente.')
  }
}

export function CSPostHogProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    // Sem chave não há cliente: identify/reset num PostHog não inicializado só
    // gera mais aviso no console.
    if (!POSTHOG_KEY) return

    const supabase = createClient()

    // Identify on mount (handles already-logged-in users)
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user) {
        posthog.identify(user.id, { email: user.email })
      }
    })

    // Identify/reset on auth state changes (login, logout)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' && session?.user) {
        posthog.identify(session.user.id, { email: session.user.email })
      } else if (event === 'SIGNED_OUT') {
        posthog.reset()
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  return <PostHogProvider client={posthog}>{children}</PostHogProvider>
}
